import { Link } from "@/i18n/navigation";
import Image from "next/image";
import { Rating, RatingButton } from "../ui/shadcn-io/rating";
import { ProductModel } from "@/types/product";
import AddToFav from "../favorite/add-to-fav";
import AddToCart from "../cart/add-to-cart";
import { useLocale } from "next-intl";
import { cn } from "@/lib/utils";

const ProductCard = ({ product }: { product: ProductModel }) => {
  const locale = useLocale();
  return (
    <Link href={`/products/${product.slug}`} className="group block">
      <div
        className="
      relative flex flex-col overflow-hidden
      rounded-2xl
      bg-white
      border border-gray-100
      shadow-[0_6px_25px_rgba(0,0,0,0.05)]
      transition-all duration-500 ease-out
      hover:-translate-y-3
      hover:shadow-[0_25px_60px_rgba(0,0,0,0.12)]
    "
      >
        {/* Shine Sweep Effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="
          absolute -left-1/2 top-0 h-full w-1/2
          bg-gradient-to-r from-transparent via-white/60 to-transparent
          skew-x-12
          translate-x-[-150%]
          group-hover:translate-x-[250%]
          transition-transform duration-1000 ease-out
        "
          />
        </div>

        {/* Floating Actions */}
        <div
          className={cn(
            "absolute top-0 x z-20 flex flex-col gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300",
            locale === "ar"
              ? "left-0 translate-x-0 sm:-translate-x-4"
              : "right-0 translate-x-0 sm:translate-x-4",
          )}
        >
          <AddToFav isFav={product.is_favorite} productId={product.id} />
        </div>

        {/* Image Section */}
        <div className="relative h-[170px] sm:h-[200px] overflow-hidden">
          {product.thumbnail_url && (
            <Image
              fill
              src={product.thumbnail_url}
              alt={product.name}
              id={`product-img-${product.id}`}
              className="
            object-cover
            transition-transform duration-700 ease-out
            group-hover:scale-105
          "
            />
          )}

          {/* Hover dark overlay */}
          <div
            className="
        absolute inset-0
        bg-primary/10 group-hover:bg-primary/15
        transition duration-500
      "
          />
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 p-4 space-y-2">
          <h3
            className="
          font-medium text-sm sm:text-base
          line-clamp-1
          transition-colors duration-300
          group-hover:text-primary
        "
          >
            {product.name}
          </h3>

          <p className="text-gray-500 text-xs line-clamp-1">
            {product.short_description}
          </p>

          <div className="hidden sm:flex items-center gap-1">
            <Rating value={product.reviews_rate} readOnly>
              {Array.from({ length: 5 }).map((_, i) => (
                <RatingButton key={i} size={14} className="text-yellow-500" />
              ))}
            </Rating>
            <span className="text-xs text-gray-400">
              ({product.reviews_rate})
            </span>
          </div>

          <div className=" flex items-center justify-between flex-wrap">
            <h4 className="text-lg font-bold">
              {product.price.toLocaleString("en-US")}
              <span className="text-sm text-gray-500 font-medium ml-1">
                {product.currency}
              </span>
            </h4>
            {/* Slide-up Add to Cart */}
            <div
              className="
          opacity-100 sm:opacity-0
          translate-y-0 sm:translate-y-10
          group-hover:translate-y-0
          group-hover:opacity-100
          transition-all duration-400 ease-out
          bg-white/90 backdrop-blur-sm
         
        "
            >
              <AddToCart
                productId={product.id}
                variants={product.variants}
                slug={product.slug}
              />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
